/**
 * Client-side impersonation state. There is no server session: the request header IS the state, so this holds the header NAME and VALUE
 * `POST /api/admin/impersonation` returned and nothing else; `/api/auth/me` stays the source of truth for who is viewed.
 * `sessionStorage`, not React state: `api.ts`'s interceptor is outside the component tree, and a reload must not silently drop back to the admin's own view.
 */
import { queryClient } from './queryClient'

const STORAGE_KEY = 'trg.impersonation'

interface ImpersonationState {
  header: string
  value: string
}

function read(): ImpersonationState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as ImpersonationState) : null
  } catch {
    return null
  }
}

let state = read()

export function impersonationHeader(): ImpersonationState | null {
  return state
}

export function startImpersonation(header: string, value: string) {
  state = { header, value }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  // Every cached query answered as the previous identity (the admin, or
  // nobody). Reset rather than invalidate, so a mid-flight response for the
  // old identity cannot land in the cache after this point.
  void queryClient.resetQueries()
}

export function stopImpersonation() {
  if (!state) return
  state = null
  sessionStorage.removeItem(STORAGE_KEY)
  void queryClient.resetQueries()
}
